import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="pipeline--booking-datetime"
//
// v1↔v2 parity (CONEK-4230): the v2 booking form no longer uses two
// datetime-local inputs — which let a user pick different days for start vs end,
// or end before start. It renders ONE day picker + a start/end time range,
// mirroring the React form (booking-form/index.tsx). This controller recomposes
// the canonical booking[start_time]/[end_time] datetime-local values the backend
// reads from (the shared day + each time-of-day), so the submitted params — and
// the backend — stay exactly the same.
//
// Source fields (UI-only, ignored by the booking strong-params):
//   input[name="booking_event_date"]        -> "YYYY-MM-DD" (ui--date-picker)
//   input[name="booking_start_time_of_day"] -> "HH:MM"      (ui--time-picker)
//   input[name="booking_end_time_of_day"]   -> "HH:MM"      (ui--time-picker)
// Composed targets (actually submitted):
//   startTime -> booking[start_time], endTime -> booking[end_time]
//
// The controller mounts on the <form>, so the child pickers' bubbling
// `ui--date-picker:change` / `ui--time-picker:change` events (and the submit)
// all reach it — see the form's data-action.
export default class extends Controller {
  static targets = ["startTime", "endTime"]

  connect() {
    // Sync once on boot. The server already rendered the initial values, so this
    // is a no-op in edit mode, but it guarantees the hidden fields match the
    // pickers if anything re-seeded them before connect.
    this.compose()
  }

  // Rebuild the two datetime-local values from the day + each time-of-day. Leaves
  // a composed field untouched when its day or time part is missing, so a
  // half-filled form never submits a broken "YYYY-MM-DDT" (the server's presence
  // validation then surfaces the empty field).
  compose() {
    const day = this._value("booking_event_date")
    const start = this._value("booking_start_time_of_day")
    const end = this._value("booking_end_time_of_day")

    if (this.hasStartTimeTarget && day && start) {
      this.startTimeTarget.value = `${day}T${start}`
    }
    if (this.hasEndTimeTarget && day && end) {
      this.endTimeTarget.value = `${day}T${end}`
    }
  }

  // CONEK-7674: keep End's minimum bound to Start at runtime, not only at render.
  // On a Start `ui--time-picker:change`, write the new Start onto End's
  // min-time value; the End picker re-applies a changed min-time value via
  // conek_ui >= 0.3.3 (ui--time-picker#minTimeValueChanged), which re-disables the
  // earlier options without touching End's value. End changes and malformed
  // values are ignored. Start is read from its input, like compose(), rather
  // than from the event payload.
  bindEndMin(event) {
    const startInput = 'input[name="booking_start_time_of_day"]'
    if (!event.target?.querySelector?.(startInput)) return

    const match = /^(\d{1,2}):(\d{2})$/.exec(
      this._value("booking_start_time_of_day"),
    )
    if (!match) return
    const hour = Number(match[1])
    const minute = Number(match[2])
    if (hour > 23 || minute > 59) return

    const endRoot = this.element
      .querySelector('input[name="booking_end_time_of_day"]')
      ?.closest('[data-controller~="ui--time-picker"]')
    if (!endRoot) return

    endRoot.setAttribute(
      "data-ui--time-picker-min-time-value",
      JSON.stringify({ hour, minute }),
    )
  }

  _value(name) {
    const el = this.element.querySelector(`input[name="${name}"]`)
    return el && el.value ? el.value.trim() : ""
  }
}
